/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { FormEventHandler, useEffect } from "react";
import Grid from "@mui/material/Grid";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import PoaAgreementText from "@/src/components/Form/Common/PowerOfAttorney/PoaAgreementText";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import { useForm } from "@/src/provider/Form/FormProvider";
import Typography from "@mui/material/Typography";
import HighlightBox from "@/src/components/Box/HighlightBox/HighlightBox";
import { Box } from "@mui/material";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { FormChangeEvent } from "@/src/types/FormDataTypes";
import MultipleSignatures from "@/src/components/Form/Common/Signature/MultipleSignatures";
import PowerOfAttorneyNextSteps from "@/src/components/Form/Common/PowerOfAttorney/PowerOfAttorneyNextSteps";
import dayjs from "dayjs";
import ProductBox from "@/src/components/ProductBox/ProductBox";

type PowerOfAttorneyProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

export default function PowerOfAttorney({ product, submitProps }: PowerOfAttorneyProps) {
  const { handleSubmit, handleChange, values } = useForm();

  const { powerOfAttorney } = values;
  useEffect(() => {
    if (powerOfAttorney === null) {
      handleChange({
        target: {
          name: "powerOfAttorney",
          value: dayjs().format("DD.MM.YYYY"),
        },
      } as FormChangeEvent);
    }
  }, [powerOfAttorney]);

  const handlePoaSubmit: FormEventHandler<HTMLFormElement> = async (event: React.FormEvent<HTMLFormElement>) => {
    if (!submitProps.isMinimal) {
      await handleSubmit(event);
      return;
    }

    await handleSubmit(event, { preventRedirect: true });
  };

  return (
    <>
      {!submitProps.isMinimal && (
        <HintBox headline={"So geht es weiter"}>
          <PowerOfAttorneyNextSteps
            bulletPoints={[
              "Sie erteilen uns die Vollmacht, damit wir Sie vertreten dürfen.",
              "Wir prüfen Ihre rechtlichen Möglichkeiten und melden uns dann umgehend bei Ihnen zurück.",
              "Wenn keine offenen Fragen mehr zu klären sind, setzen wir uns mit Ihrem Vermieter in Verbindung und versuchen Ihren Fall außergerichtlich zu klären.",
              "Sollte eine außergerichtliche Klärung nicht möglich sein, vertreten wir Sie selbstverständlich auch im gerichtlichen Verfahren.",
            ]}
          />
          {product.caseInsurance.iIWPI && (
            <HighlightBox textColor={"defaultText"} headline={""} textAlign={"center"}>
              {product.caseInsurance.deductibleMayApply
                ? `Die ${product.caseInsurance.insurer?.name} übernimmt die Kosten des weiteren Vorgehens gemäß Ihren Versicherungsbedingungen. Eine eventuell anfallende Selbstbeteiligung tragen Sie selbst.`
                : `Sie sind bei der ${product.caseInsurance.insurer?.name} rechtsschutzversichert, daher ist das weitere Vorgehen für Sie komplett kostenlos!`}
            </HighlightBox>
          )}
          {product.caseInsurance.iIWPI === false && product.legalExpenseInsurance && (
            <HighlightBox textColor={"defaultText"} headline={""} textAlign={"center"}>
              Sie haben angegeben, dass Sie bei {product.caseInsurance.insurer?.name} rechtsschutzversichert sind, daher müssen Sie sich über Kosten keinerlei
              Sorgen machen! Wir stellen eine Deckungsanfrage bei Ihrer Versicherung und kümmern uns um alles Weitere bezüglich der Abrechnung.
            </HighlightBox>
          )}
        </HintBox>
      )}
      <ProductBox variant={"hintbox"} headline={<strong>Vollmacht</strong>}>
        <Box component={"form"} id={"application-form"} onSubmit={handlePoaSubmit}>
          <div>
            <Typography sx={{
              marginBottom: "16px"
            }}>
              Hiermit {product.additionalClients!.length ? "erteilen wir" : "erteile ich"} der Legalbird Rechtsanwälte Rechtsanwalts GmbH Vollmacht zu{" "}
              {product.additionalClients!.length ? "unserer" : "meiner"} Verteidigung und Vertretung, mit der Ermächtigung zur Bestellung von
              Unterbevollmächtigten.
            </Typography>
            <Typography sx={{
              marginBottom: "16px"
            }}>
              Die Vollmacht gilt in Sachen {product.clientVsOpponentString} wegen aller Ansprüche aus dem Mietverhältnis zwischen den zuvor genannten Parteien
              aus dem Mietvertrag vom {dayjs(product.contractDate).format("DD.MM.YYYY")}.
            </Typography>
            <Typography sx={{
              marginBottom: "16px"
            }}>Die Vollmacht ermächtigt zu allen die Angelegenheit betreffenden Handlungen, insbesondere zur</Typography>
            <ul>
              <li>
                <Typography>außergerichtlichen Interessensvertretung,</Typography>
              </li>
              <li>
                <Typography>Prozessführung einschließlich Einlegung und Rücknahme von Rechtsmitteln,</Typography>
              </li>
              <li>
                <Typography>Entgegennahme von Wertsachen und Übergabe der Mietsachen,</Typography>
              </li>
              <li>
                <Typography>Entgegennahme von Zustellungen,</Typography>
              </li>
              <li>
                <Typography>Empfangnahme von Geldern,</Typography>
              </li>
              <li>
                <Typography>Ausspruch jeglicher Form der Beendigung des Mietverhältnisses und</Typography>
              </li>
              <li>
                <Typography>
                  Begründung und Aufhebung von Vertragsverhältnissen und Abgabe von einseitigen Willenserklärungen, insbesondere zum Ausspruch von ordentlichen
                  und außerordentlichen Kündigungen.
                </Typography>
              </li>
            </ul>
            <Typography sx={{
              marginBottom: "16px"
            }}>Die Vollmacht erstreckt sich auch auf Nebenverfahren mit Ausnahme der Zwangsvollstreckung.</Typography>
            <Typography sx={{
              marginBottom: "16px"
            }}>
              {product.additionalClients!.length ? "Uns" : "Mir"} ist bekannt, dass mit der Vollmacht eine Beauftragung zu{" "}
              {product.additionalClients!.length ? "unserer" : "meiner"} Verteidigung und damit eine Anwaltsvergütung entsteht, deren Höhe sich nach den
              gesetzlichen Vorschriften richtet.
            </Typography>
            <PoaAgreementText lawFirmName={product.lawFirm.name} multipleClients={!!product.additionalClients!.length} />
          </div>
          {!product.clientIsCustomer && (
            <Box sx={{ marginTop: "1rem" }}>
              <HighlightBox textColor={"defaultText"} headline={""}>
                <Typography>
                  Achtung: Hier muss die Person unterschreiben, die den Vertrag geschlossen hat. In Ihrem Fall also:{" "}
                  {product.client.gender === "male" ? "Herr" : "Frau"} {product.client.givenName} {product.client.familyName}
                </Typography>
              </HighlightBox>
            </Box>
          )}

          <MultipleSignatures product={product} signatories={product.signatories ? product.signatories : [product.client]} />

          <Grid container>
            <Grid size={12}>
              <ButtonLoading
                color="primary"
                variant="contained"
                type="submit"
                fullWidth={true}
                disabled={!product.client.signature}
                isLoading={submitProps.isLoading}
              >
                Vollmacht erteilen und Fall prüfen lassen
              </ButtonLoading>
            </Grid>
          </Grid>
          {product.caseInsurance.iIWPI === false && product.legalExpenseInsurance && (
            <Box
              sx={{
                paddingTop: 3,
                textAlign: "center"
              }}>
              <HighlightBox textColor={"defaultText"} headline={""}>
                Sie haben angegeben, dass Sie bei {product.caseInsurance.insurer?.name} rechtsschutzversichert sind, daher müssen Sie sich über Kosten keinerlei
                Sorgen machen! Wir stellen eine Deckungsanfrage bei Ihrer Versicherung und kümmern uns um alles Weitere bezüglich der Abrechnung.
              </HighlightBox>
            </Box>
          )}
        </Box>
      </ProductBox>
    </>
  );
}
